import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Mic, MicOff, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const VoiceInput = ({ 
  onTranscript, 
  targetInputId = null,
  className = '',
  size = 'icon'
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'pt-BR';

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      const currentTranscript = finalTranscript || interimTranscript;
      setTranscript(currentTranscript);

      if (finalTranscript) {
        onTranscript?.(finalTranscript);
        
        // If targeting a specific input, update it
        if (targetInputId) {
          const input = document.getElementById(targetInputId);
          if (input) {
            const currentValue = input.value;
            const newValue = currentValue ? `${currentValue} ${finalTranscript}` : finalTranscript;
            
            // Trigger input event for React
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set 
              || Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
            
            if (nativeInputValueSetter) {
              nativeInputValueSetter.call(input, newValue);
              input.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        }
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        toast.error('Permissão de microfone negada. Habilite nas configurações do navegador.');
      } else if (event.error === 'no-speech') {
        toast.info('Nenhuma fala detectada. Tente novamente.');
      } else {
        toast.error(`Erro no reconhecimento de voz: ${event.error}`);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      if (isListening) {
        // Restart if still supposed to be listening
        try {
          recognition.start();
        } catch (e) {
          setIsListening(false);
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.stop();
    };
  }, [onTranscript, targetInputId, isListening]);

  const startListening = useCallback(() => {
    if (!recognitionRef.current) return;
    
    try {
      setTranscript('');
      recognitionRef.current.start();
      setIsListening(true);
      toast.success('Escutando... Fale agora.');
    } catch (error) {
      console.error('Error starting recognition:', error);
      toast.error('Erro ao iniciar reconhecimento de voz');
    }
  }, []);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;
    
    recognitionRef.current.stop();
    setIsListening(false);
    toast.info('Ditado finalizado');
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript('');
    if (targetInputId) {
      const input = document.getElementById(targetInputId);
      if (input) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set 
          || Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
        
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(input, '');
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    }
  }, [targetInputId]);

  if (!isSupported) {
    return (
      <Button
        type="button"
        variant="ghost"
        size={size}
        disabled
        title="Reconhecimento de voz não suportado neste navegador"
        className={className}
      >
        <MicOff className="h-4 w-4 text-muted-foreground" />
      </Button>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <Button
        type="button"
        variant={isListening ? "destructive" : "ghost"}
        size={size}
        onClick={isListening ? stopListening : startListening}
        title={isListening ? "Parar ditado" : "Iniciar ditado"}
        className={isListening ? "animate-pulse" : ""}
      >
        {isListening ? (
          <Mic className="h-4 w-4" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
      </Button>
      
      {transcript && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={clearTranscript}
          title="Limpar"
          className="h-6 w-6"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};

export default VoiceInput;
